import { Anchor, Badge, Group, Stack, Text, Title } from '@mantine/core';
import { Link, useParams } from 'react-router';
import { useCompanies, useLocations, useLokasyonKapisi, useWorkplacesTumu } from '../api/queries';
import { ISYERI_DURUM_ETIKET, TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import { tarihGoster } from '../lib/format';
import { Bolum, KapiSebebi } from '../sozlesme';
import { LokasyonBolumu } from './organizasyon-parts';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';

/**
 * İŞYERİ KARTI (TUR 9 / B1 — P1: *"kendi sayfası olabiliyor mu?"*).
 *
 * İşyeri **bağımsız bir kayıttır**: künyesi (sicil · NACE · tehlike sınıfı ·
 * faaliyet başlangıcı · durum), altında **lokasyon ağacı** ve çalışan sayısı
 * vardır — bunların hepsi bir tablo satırına sığmaz. Lokasyon düğümü ise
 * **alt kalemdir** → satır içi girilir (P1-a).
 *
 * ⚠️ **TEHLİKE SINIFI GÖSTERİLİR, SORULMAZ** (Kapsam §3.2): NACE kodundan
 * TÜRETİLİR ve girdi olarak kabul edilmez.
 * ⚠️ **ÇALIŞAN SAYISI BİR KOLON DEĞİL, HER OKUMADA TÜRETİLEN SAYIDIR** — kurul
 * (50+) ve ekip eşikleri onu okur; yüklenici personeli SAYIYA GİRMEZ.
 * ⚠️ **ARŞİVLİ İŞYERİNDE OKUMA SERBESTTİR** (§2.15) — arşivin varlık sebebi
 * denetim/geçmiş erişimidir; kapanan yalnız YAZMADIR ve kapı sunucudadır.
 */
export function IsyeriDetayScreen(): React.JSX.Element {
  const { workplaceId } = useParams<{ workplaceId: string }>();
  const isyerleri = useWorkplacesTumu();
  const companies = useCompanies();
  const isyeri = isyerleri.data?.find((w) => w.id === workplaceId);
  const lokasyonlar = useLocations(workplaceId ?? '', { enabled: Boolean(workplaceId) });
  // ✏️ P13 · B1/1 — bayrak VE sebep POST/PATCH uçlarının adım listesinden
  // (şapka → bahçe → arşiv). Ekranda şapka listesi yazılmaz, arşiv türetilmez.
  const lokasyonKapisi = useLokasyonKapisi(workplaceId ?? '', { enabled: Boolean(workplaceId) });
  const yazabilir = lokasyonKapisi.data?.yazabilir ?? false;

  if (isyerleri.isSuccess && !isyeri) {
    return (
      <Stack>
        <Title order={3}>İşyeri</Title>
        <BosListeNotu mesaj="İşyeri bulunamadı ya da bahçenizin dışında." />
        <Anchor component={Link} to="/organizasyon" size="sm">
          ← Şirketlere dön
        </Anchor>
      </Stack>
    );
  }

  return (
    <Stack>
      <Anchor component={Link} to="/organizasyon" size="sm" data-testid="isyeri-geri">
        ← Şirketler
      </Anchor>
      <QueryStatus queries={[isyerleri, lokasyonlar]} />
      {isyeri && (
        <>
          <Stack gap={4} data-testid="isyeri-kunye">
            <Title order={3} data-testid="isyeri-ad">
              {isyeri.ad}
            </Title>
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {companies.data?.find((c) => c.id === isyeri.companyId)?.ad ?? 'Şirket'}
              </Text>
              <Badge
                variant="light"
                color={
                  isyeri.tehlikeSinifi === 'highly_hazardous'
                    ? ANLAM.engel
                    : isyeri.tehlikeSinifi === 'hazardous'
                      ? ANLAM.dikkat
                      : ANLAM.olumlu
                }
                data-testid="isyeri-detay-tehlike"
              >
                {TEHLIKE_SINIFI_ETIKET[isyeri.tehlikeSinifi]}
              </Badge>
              <Badge
                variant="light"
                color={isyeri.durum === 'active' ? ANLAM.olumlu : ANLAM.bilgi}
                data-testid="isyeri-detay-durum"
              >
                {ISYERI_DURUM_ETIKET[isyeri.durum]}
              </Badge>
            </Group>
            <Text size="sm" data-testid="isyeri-detay-sicil">
              SGK sicil: {isyeri.sgkSicil} · NACE: {isyeri.naceKod} · Çalışan:{' '}
              {isyeri.calisanSayisi}
              {isyeri.faaliyetBaslangic
                ? ` · Faaliyet başlangıcı: ${tarihGoster(isyeri.faaliyetBaslangic)}`
                : ''}
            </Text>
            <Text size="xs" c="dimmed">
              Tehlike sınıfı NACE kodundan türetilir; çalışan sayısı her okumada hesaplanır ve
              yüklenici personelini saymaz.
            </Text>
          </Stack>

          <Bolum baslik="Lokasyon ağacı" testId="isyeri-lokasyonlar">
            <LokasyonBolumu
              workplaceId={isyeri.id}
              dugumler={lokasyonlar.data ?? []}
              yazilabilir={yazabilir}
            />
            {lokasyonKapisi.data && (
              <KapiSebebi
                durum={lokasyonKapisi.data}
                nesne="Lokasyon düğümünü"
                fiil="ekler"
                testId="isyeri-lokasyon-yazamama-sebebi"
              />
            )}
          </Bolum>
        </>
      )}
    </Stack>
  );
}
